import * as Headless from '@headlessui/react';
import { InformationCircleIcon } from '@heroicons/react/16/solid';
import clsx from 'clsx';

import { CaraWorkspaceEffortLevel, type CaraWorkspaceFieldsFragment } from '~/api/graphql';
import { FEATURE_FLAGS, useFlag } from '~/components/feature-flags';
import {
  effortLevelDescription,
  effortLevelLabel,
  effortRaisedWithinQuick,
  FALLBACK_EFFORT_LEVEL
} from '~/components/presets/effort-level';
import { AutoEffortIcon } from '~/components/presets/effort-level-select';

type EffortWorkspace = Pick<
  CaraWorkspaceFieldsFragment,
  'effortLevel' | 'effortRaisedReason' | 'preset' | 'customPreset'
> & {
  messages: ReadonlyArray<
    Pick<CaraWorkspaceFieldsFragment['messages'][number], 'role' | 'useCase'>
  >;
};

// Shaped like the compose box's effort pill, so the control the level was
// picked from and the one that shows it read as the same thing.
const TRIGGER_CLASS = clsx(
  'inline-flex h-8 items-center gap-1 rounded-full border border-neutral-200 bg-white pl-3 pr-2',
  'text-xs font-medium text-neutral-700 transition-colors',
  'hover:border-neutral-300 hover:bg-neutral-100 data-[open]:bg-neutral-100',
  'focus:outline-none data-[focus]:ring-2 data-[focus]:ring-primary-500',
  'dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-300',
  'dark:hover:border-white/20 dark:hover:bg-white/10 dark:data-[open]:bg-white/10'
);

const PANEL_CLASS = clsx(
  'z-[100] w-80 rounded-xl bg-white p-3 shadow-lg ring-1 ring-black/5 focus:outline-none',
  'transition duration-100 ease-out data-[closed]:scale-95 data-[closed]:opacity-0',
  'dark:bg-neutral-800 dark:ring-white/10'
);

/**
 * The level a workspace runs at, a raise included. Auto stands until the
 * server replaces it with the level it chose, and a workspace with no stored
 * level ran at the default. A Quick workspace that Cara gave more power partway
 * through still reads as Quick.
 */
const runningLevel = (workspace: EffortWorkspace): CaraWorkspaceEffortLevel => {
  if (workspace.effortRaisedReason && !effortRaisedWithinQuick(workspace)) {
    return FALLBACK_EFFORT_LEVEL;
  }
  return workspace.effortLevel ?? FALLBACK_EFFORT_LEVEL;
};

/**
 * The analysis level of an existing workspace, shown in its follow-up box
 * where the effort picker sat when it was started. Read-only: the level binds
 * the model for every turn, so it is fixed once the workspace exists.
 */
export const WorkspaceEffortLevelBadge = ({ workspace }: { workspace: EffortWorkspace }) => {
  const enabled = useFlag(FEATURE_FLAGS.EFFORT_LEVEL_SELECTOR);
  if (!enabled) {
    return null;
  }

  const name = 'Analysis depth';
  const level = runningLevel(workspace);
  const shown = effortLevelLabel(level);
  const auto = level === CaraWorkspaceEffortLevel.Auto;

  return (
    <Headless.Popover className="relative">
      <Headless.PopoverButton
        aria-label={`${name}: ${shown}`}
        className={clsx(TRIGGER_CLASS, auto && 'pl-2.5')}
      >
        {auto && <AutoEffortIcon />}
        {shown}
        <InformationCircleIcon
          aria-hidden="true"
          className="size-4 text-neutral-400 dark:text-neutral-500"
        />
      </Headless.PopoverButton>
      <Headless.PopoverPanel transition anchor={{ to: 'top end', gap: 8 }} className={PANEL_CLASS}>
        <p className="text-sm font-semibold text-neutral-950 dark:text-white">{shown}</p>
        <p className="mt-0.5 text-xs/5 text-neutral-500 dark:text-neutral-400">
          {effortLevelDescription(level)}
        </p>
        {auto && (
          <p className="mt-2 text-xs/5 text-neutral-700 dark:text-neutral-300">
            Cara picks the level once it starts working.
          </p>
        )}
        <p className="mt-2 border-t border-neutral-950/5 pt-2 text-xs/5 text-neutral-500 dark:border-white/10 dark:text-neutral-400">
          This level is set when a workspace starts and stays the same for every message. Start a
          new workspace to use a different level.
        </p>
      </Headless.PopoverPanel>
    </Headless.Popover>
  );
};
